// Portal App - Main entry point for portal routes
import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import PortalLayout from './components/PortalLayout';
import { ForcePasswordChange } from './components/ForcePasswordChange';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CasesListPage from './pages/CasesListPage';
import RenewalTrackingPage from './pages/RenewalTrackingPage';
import CaseFormPage from './pages/CaseFormPage';
import CaseDetailPage from './pages/CaseDetailPage';
import UsersPage from './pages/UsersPage';
import CoverageYearsPage from './pages/CoverageYearsPage';
import BulkImportPage from './pages/BulkImportPage';
import ClientsPage from './pages/ClientsPage';
import ReportsPage from './pages/ReportsPage';
import TrashPage from './pages/TrashPage';
import ClientDetailPage from './pages/ClientDetailPage';
import { StandardClientIntakePage } from './pages/StandardClientIntakePage';
import ProducersPage from './pages/ProducersPage';
import { WorkspaceSelectionPage } from './pages/WorkspaceSelectionPage';
import WorkspacesPage from './pages/WorkspacesPage';
import SecurityPage from './pages/SecurityPage';
import AuditLogPage from './pages/AuditLogPage';
import AccessRequestsPage from './pages/AccessRequestsPage';
import TeamPage from './pages/TeamPage';
import OscarPage from './pages/OscarPage';
import { Loader2 } from 'lucide-react';

// Ensures an active workspace is chosen before entering the portal.
function WorkspaceGate({ children }) {
  const { workspacesLoaded, workspaces, activeWorkspaceId, setActiveWorkspace } = useAuth();

  useEffect(() => {
    if (workspacesLoaded && !activeWorkspaceId && workspaces.length === 1) {
      setActiveWorkspace(workspaces[0].id);
    }
  }, [workspacesLoaded, activeWorkspaceId, workspaces, setActiveWorkspace]);

  if (!workspacesLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  if (workspaces.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6 text-center">
        <p className="text-slate-600">Your account has no workspace access. Please contact a Calyvra Administrator.</p>
      </div>
    );
  }

  if (!activeWorkspaceId) {
    if (workspaces.length === 1) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
        </div>
      );
    }
    return <WorkspaceSelectionPage />;
  }

  return children;
}

// Protected Route Component
function ProtectedRoute({ children, permission, anyPermission, roles }) {
  const { user, loading, hasPermission } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/portal/login" replace />;
  }

  // First-login: force a secure password before accessing anything
  if (user.must_change_password) {
    return <ForcePasswordChange />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to="/portal" replace />;
  }

  if (anyPermission && !anyPermission.some((p) => hasPermission(p))) {
    return <Navigate to="/portal" replace />;
  }

  if (roles) {
    const mine = new Set([user.role, ...Object.values(user.workspace_roles || {}).flat()]);
    if (!roles.some((r) => mine.has(r))) {
      return <Navigate to="/portal" replace />;
    }
  }

  return <WorkspaceGate><PortalLayout>{children}</PortalLayout></WorkspaceGate>;
}

function PortalRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route path="/" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />

      <Route path="/cases" element={
        <ProtectedRoute>
          <CasesListPage />
        </ProtectedRoute>
      } />

      <Route path="/renewals" element={
        <ProtectedRoute>
          <RenewalTrackingPage />
        </ProtectedRoute>
      } />

      <Route path="/clients" element={
        <ProtectedRoute>
          <ClientsPage />
        </ProtectedRoute>
      } />

      <Route path="/reports" element={
        <ProtectedRoute roles={['admin', 'ls_admin', 'program_manager']}>
          <ReportsPage />
        </ProtectedRoute>
      } />

      <Route path="/geography" element={<Navigate to="/portal/reports?tab=geography" replace />} />

      <Route path="/trash" element={
        <ProtectedRoute roles={['admin']}>
          <TrashPage />
        </ProtectedRoute>
      } />

      <Route path="/clients/new" element={
        <ProtectedRoute permission="clients.manage">
          <StandardClientIntakePage />
        </ProtectedRoute>
      } />

      <Route path="/clients/:id" element={
        <ProtectedRoute>
          <ClientDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/producers" element={
        <ProtectedRoute permission="producers.manage">
          <ProducersPage />
        </ProtectedRoute>
      } />

      <Route path="/cases/new" element={
        <ProtectedRoute permission="cases.create">
          <CaseFormPage />
        </ProtectedRoute>
      } />

      <Route path="/cases/:id/edit" element={
        <ProtectedRoute permission="cases.edit">
          <CaseFormPage />
        </ProtectedRoute>
      } />

      <Route path="/cases/:id" element={
        <ProtectedRoute>
          <CaseDetailPage />
        </ProtectedRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute anyPermission={['users.manage', 'users.deactivate']}>
          <UsersPage />
        </ProtectedRoute>
      } />

      <Route path="/workspaces" element={
        <ProtectedRoute permission="workspaces.manage">
          <WorkspacesPage />
        </ProtectedRoute>
      } />

      <Route path="/coverage-years" element={
        <ProtectedRoute permission="years.manage">
          <CoverageYearsPage />
        </ProtectedRoute>
      } />

      <Route path="/import" element={
        <ProtectedRoute permission="bulk.import">
          <BulkImportPage />
        </ProtectedRoute>
      } />

      <Route path="/security" element={
        <ProtectedRoute>
          <SecurityPage />
        </ProtectedRoute>
      } />

      <Route path="/audit-logs" element={
        <ProtectedRoute permission="workspaces.manage">
          <AuditLogPage />
        </ProtectedRoute>
      } />

      <Route path="/access-requests" element={
        <ProtectedRoute permission="workspaces.manage">
          <AccessRequestsPage />
        </ProtectedRoute>
      } />

      <Route path="/team" element={
        <ProtectedRoute permission="workspaces.manage">
          <TeamPage />
        </ProtectedRoute>
      } />

      <Route path="/oscar" element={
        <ProtectedRoute roles={['admin']}>
          <OscarPage />
        </ProtectedRoute>
      } />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/portal" replace />} />
    </Routes>
  );
}

export function PortalApp() {
  return (
    <AuthProvider>
      <PortalRoutes />
    </AuthProvider>
  );
}

export default PortalApp;
